import { useState, useMemo, useCallback } from 'react';
import { adminApi } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Loading, ApiError, EmptyState } from '../components/ui/Loading';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useToast } from '../context/ToastContext';
import { formatPrice, formatDate, formatDateTime, paginate } from '../utils/helpers';

// ─── Statuts ──────────────────────────────────────────────────────────────────
const STATUS = {
  open:                 { label: 'En cours',               cls: 'badge-gray',   icon: '⏳' },
  pending_confirmation: { label: 'En attente confirmation', cls: 'badge-yellow', icon: '🕐' },
  settled:              { label: 'Réglé',                  cls: 'badge-green',  icon: '✅' },
};

// ─── Normalisation montant livreur ────────────────────────────────────────────
const getNetOwed = (row) =>
  row.net_owed_to_lavex ?? row.amount_due_to_lavex ?? row.amount_due ?? 0;

// ─── Bouton Facture ───────────────────────────────────────────────────────────
const InvoiceButton = ({ type, settlementId }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const handleClick = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getSettlementInvoice(type, settlementId);
      const html = res?.data?.invoice_html || res?.invoice_html;
      const url  = res?.data?.pdf_url      || res?.pdf_url;
      if (html) {
        const w = window.open('', '_blank');
        if (!w) { toast.error('Autorisez les popups pour imprimer la facture.'); return; }
        w.document.open();
        w.document.write(`<!DOCTYPE html><html><head>
          <meta charset="UTF-8"><title>Facture LAVEX #${settlementId}</title>
          <style>body{font-family:Arial,sans-serif;margin:20px;color:#111}@media print{.no-print{display:none}}</style>
        </head><body>
          <div class="no-print" style="margin-bottom:16px">
            <button onclick="window.print()" style="padding:8px 16px;background:#5b6af0;color:#fff;border:none;border-radius:6px;cursor:pointer">🖨️ Imprimer</button>
            <button onclick="window.close()" style="padding:8px 16px;margin-left:8px;border:1px solid #ccc;border-radius:6px;cursor:pointer">Fermer</button>
          </div>${html}</body></html>`);
        w.document.close();
        w.onload = () => w.print();
      } else if (url) {
        window.open(url, '_blank');
      } else {
        toast.error('Endpoint facture non implémenté côté backend');
      }
    } catch (err) {
      toast.error(err.message?.includes('404') ? 'Facture non disponible' : err.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [type, settlementId, toast]);

  return (
    <button className="btn btn-secondary btn-sm" onClick={handleClick} disabled={loading}>
      {loading ? <span className="spinner" style={{ width: 12, height: 12 }} /> : '🖨️ Facture'}
    </button>
  );
};

// ─── Modale détail règlement ──────────────────────────────────────────────────
const SettlementDetailModal = ({ row, type, open, onClose, onAction, acting }) => {
  if (!row) return null;

  const netOwed = getNetOwed(row);
  const isNeg   = netOwed < 0;
  const s       = STATUS[row.status] || STATUS.open;

  const periodLabel = row.period_label
    || (row.week_start  ? `${formatDate(row.week_start)} → ${formatDate(row.week_end)}`   : null)
    || (row.month_start ? `${formatDate(row.month_start)} → ${formatDate(row.month_end)}` : null)
    || '—';

  // Décrire le flux selon le type et le sens
  const fluxDescription = () => {
    if (type === 'livreurs') {
      if (isNeg) return {
        title: '💸 LAVEX doit verser au livreur',
        color: 'var(--blue)',
        steps: [
          { done: true,  label: 'Règlement hebdo calculé (cron)' },
          { done: row.status !== 'open', label: `Admin envoie le virement : ${formatPrice(Math.abs(netOwed))} au livreur` },
          { done: row.status === 'settled', label: 'Admin clique "Marquer versé" → statut settled' },
        ],
        adminAction: row.status === 'open' ? 'settle-negative' : null,
        adminBtnLabel: '💸 Marquer versé au livreur',
        adminBtnStyle: 'btn-primary',
        note: 'Le livreur a gagné plus en commissions qu\'il n\'a encaissé en espèces. LAVEX lui reverse la différence.',
      };
      return {
        title: '💰 Livreur doit reverser à LAVEX',
        color: 'var(--orange)',
        steps: [
          { done: true,  label: 'Règlement hebdo calculé (cron)' },
          { done: ['pending_confirmation','settled'].includes(row.status), label: `Livreur paie via l'app mobile : ${formatPrice(netOwed)}` },
          { done: row.status === 'settled', label: 'Admin confirme la réception → statut settled' },
        ],
        adminAction: row.status === 'pending_confirmation' ? 'confirm' : null,
        adminBtnLabel: '✓ Confirmer réception du paiement',
        adminBtnStyle: 'btn-success',
        note: 'Le livreur a encaissé des espèces pour LAVEX. Il doit reverser : espèces collectées − ses commissions.',
      };
    }
    if (type === 'laveries') return {
      title: '💸 LAVEX verse à la laverie',
      color: 'var(--green)',
      steps: [
        { done: true,  label: 'Règlement mensuel calculé (cron)' },
        { done: row.status === 'settled', label: `Admin envoie le virement : ${formatPrice(row.net_profit ?? 0)} au RIB de la laverie` },
        { done: row.status === 'settled', label: 'Admin clique "Marquer réglé" → statut settled' },
      ],
      adminAction: row.status === 'open' ? 'settle' : null,
      adminBtnLabel: '💸 Marquer réglé (virement envoyé)',
      adminBtnStyle: 'btn-primary',
      note: 'La laverie ne paie jamais LAVEX. LAVEX lui reverse ses revenus après déduction de la commission (15%).',
    };
    // enterprises
    return {
      title: '💰 Entreprise paie LAVEX',
      color: 'var(--purple)',
      steps: [
        { done: true,  label: 'Facture mensuelle générée' },
        { done: ['pending_confirmation','settled'].includes(row.status), label: `Entreprise vire : ${formatPrice(row.amount_due ?? 0)}` },
        { done: row.status === 'settled', label: 'Admin confirme la réception → statut settled' },
      ],
      adminAction: row.status === 'pending_confirmation' ? 'confirm' : null,
      adminBtnLabel: '✓ Confirmer paiement reçu',
      adminBtnStyle: 'btn-success',
      note: 'L\'entreprise reçoit une facture mensuelle. Elle vire le montant, puis l\'admin confirme la réception.',
    };
  };

  const flux = fluxDescription();

  return (
    <Modal open={open} onClose={onClose}
      title={`Règlement — ${row.livreur_name || row.laverie_name || row.enterprise_name}`}
      size="md"
      footer={<>
        <InvoiceButton type={type} settlementId={row.settlement_id} />
        <button className="btn btn-secondary" style={{ marginLeft: 'auto' }} onClick={onClose}>
          Fermer
        </button>
        {flux.adminAction && (
          <button
            className={`btn ${flux.adminBtnStyle}`}
            onClick={() => { onAction(flux.adminAction, row); onClose(); }}
            disabled={acting === row.settlement_id}
          >
            {acting === row.settlement_id
              ? <span className="spinner" style={{ width: 14, height: 14 }} />
              : flux.adminBtnLabel}
          </button>
        )}
      </>}
    >
      {/* En-tête flux */}
      <div style={{
        background: `${flux.color}10`,
        border: `1px solid ${flux.color}30`,
        borderRadius: 10, padding: '12px 16px', marginBottom: 20,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: flux.color }}>{flux.title}</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3 }}>{flux.note}</div>
        </div>
        <Badge className={s.cls} style={{ flexShrink: 0 }}>{s.icon} {s.label}</Badge>
      </div>

      {/* Timeline du flux */}
      <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>
        Progression
      </div>
      <div style={{ position: 'relative', paddingLeft: 24, marginBottom: 20 }}>
        <div style={{ position: 'absolute', left: 9, top: 8, bottom: 8, width: 2, background: 'var(--border)' }} />
        {flux.steps.map((step, i) => (
          <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'flex-start' }}>
            <div style={{
              width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginTop: 1,
              background: step.done ? flux.color : 'var(--border)',
              border: `2px solid ${step.done ? flux.color : 'var(--border2)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, color: '#fff', zIndex: 1,
            }}>
              {step.done ? '✓' : ''}
            </div>
            <div style={{
              fontSize: 13,
              color: step.done ? 'var(--text)' : 'var(--text3)',
              fontWeight: step.done ? 500 : 400,
            }}>
              {step.label}
            </div>
          </div>
        ))}
      </div>

      {/* Détails financiers */}
      <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
        Détails financiers
      </div>
      <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '14px 16px' }}>
        {type === 'livreurs' && [
          { l: 'Période',              v: periodLabel },
          { l: 'Espèces collectées',   v: formatPrice(row.total_collected ?? 0),       color: 'var(--text)' },
          { l: 'Commission gagnée',    v: `+${formatPrice(row.total_commission_earned ?? 0)}`, color: 'var(--green)' },
          { l: netOwed >= 0 ? 'Net à reverser à LAVEX' : 'Net que LAVEX doit verser',
                                       v: formatPrice(Math.abs(netOwed)),
                                       color: netOwed >= 0 ? 'var(--red)' : 'var(--blue)', bold: true },
        ].map((r, i) => (
          <div key={i} className="info-row" style={{ padding: '5px 0' }}>
            <span className="info-label">{r.l}</span>
            <span className="info-value" style={{ color: r.color, fontWeight: r.bold ? 700 : 400 }}>{r.v}</span>
          </div>
        ))}

        {type === 'laveries' && [
          { l: 'Période',              v: periodLabel },
          { l: 'Revenus services',     v: formatPrice(row.total_service_revenue ?? 0) },
          { l: 'Commission LAVEX (−)', v: `-${formatPrice(row.total_commission_lavex ?? 0)}`, color: 'var(--red)' },
          { l: 'Profit net (à verser)',v: formatPrice(row.net_profit ?? 0), color: 'var(--green)', bold: true },
          ...(row.rib ? [{ l: 'RIB', v: row.rib }] : []),
        ].map((r, i) => (
          <div key={i} className="info-row" style={{ padding: '5px 0' }}>
            <span className="info-label">{r.l}</span>
            <span className="info-value" style={{ color: r.color, fontWeight: r.bold ? 700 : 400 }}>{r.v}</span>
          </div>
        ))}

        {type === 'enterprises' && [
          { l: 'Période',        v: periodLabel },
          { l: 'Montant dû',    v: formatPrice(row.amount_due ?? 0), color: 'var(--accent)', bold: true },
          ...(row.payment_date ? [{ l: 'Paiement reçu le', v: formatDateTime(row.payment_date) }] : []),
        ].map((r, i) => (
          <div key={i} className="info-row" style={{ padding: '5px 0' }}>
            <span className="info-label">{r.l}</span>
            <span className="info-value" style={{ color: r.color, fontWeight: r.bold ? 700 : 400 }}>{r.v}</span>
          </div>
        ))}
      </div>
    </Modal>
  );
};

// ─── Ligne du tableau ─────────────────────────────────────────────────────────
const SettlementRow = ({ row, type, onAction, acting, onOpenDetail }) => {
  const s       = STATUS[row.status] || STATUS.open;
  const netOwed = getNetOwed(row);
  const isNeg   = netOwed < 0;

  const periodLabel = row.period_label
    || (row.week_start  ? `${formatDate(row.week_start)} → ${formatDate(row.week_end)}`   : null)
    || (row.month_start ? `${formatDate(row.month_start)} → ${formatDate(row.month_end)}` : null)
    || '—';

  return (
    <tr style={{ cursor: 'pointer' }} onClick={() => onOpenDetail(row)}>
      <td onClick={e => e.stopPropagation()}>
        <div className="td-main">
          {row.livreur_name || row.laverie_name || row.enterprise_name || `#${row.settlement_id}`}
        </div>
        <div className="td-sub">{periodLabel}</div>
      </td>

      {type === 'livreurs' && (
        <>
          <td style={{ color: 'var(--text2)', fontSize: 13 }}>{formatPrice(row.total_collected ?? 0)}</td>
          <td style={{ color: 'var(--green)', fontWeight: 600 }}>+{formatPrice(row.total_commission_earned ?? 0)}</td>
          <td>
            {isNeg ? (
              <div>
                <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--blue)' }}>
                  LAVEX doit {formatPrice(Math.abs(netOwed))}
                </span>
                <div className="td-sub">à verser au livreur</div>
              </div>
            ) : (
              <div>
                <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--red)' }}>
                  {formatPrice(netOwed)}
                </span>
                <div className="td-sub">à reverser à LAVEX</div>
              </div>
            )}
          </td>
        </>
      )}

      {type === 'laveries' && (
        <>
          <td style={{ color: 'var(--text2)', fontSize: 13 }}>{formatPrice(row.total_service_revenue ?? 0)}</td>
          <td style={{ color: 'var(--red)', fontWeight: 600 }}>-{formatPrice(row.total_commission_lavex ?? 0)}</td>
          <td>
            <span style={{ fontWeight: 700, color: 'var(--green)', fontSize: 14 }}>{formatPrice(row.net_profit ?? 0)}</span>
            <div className="td-sub">à verser à la laverie</div>
          </td>
        </>
      )}

      {type === 'enterprises' && (
        <td>
          <span style={{ fontWeight: 700, color: 'var(--accent)', fontSize: 14 }}>{formatPrice(row.amount_due ?? 0)}</span>
          <div className="td-sub">à recevoir de l'entreprise</div>
        </td>
      )}

      <td><Badge className={s.cls}>{s.icon} {s.label}</Badge></td>

      {/* Actions rapides dans le tableau — cliquer sur la ligne ouvre le détail */}
      <td onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <InvoiceButton type={type} settlementId={row.settlement_id} />

          {/* Flux A positif : livreur a payé → confirmer réception */}
          {type === 'livreurs' && !isNeg && row.status === 'pending_confirmation' && (
            <button className="btn btn-success btn-sm"
              onClick={() => onAction('confirm', row)}
              disabled={acting === row.settlement_id}>
              {acting === row.settlement_id
                ? <span className="spinner" style={{ width: 12, height: 12 }} />
                : '✓ Confirmer réception'}
            </button>
          )}

          {/* Flux A négatif : LAVEX doit payer le livreur */}
          {type === 'livreurs' && isNeg && row.status === 'open' && (
            <button className="btn btn-primary btn-sm"
              onClick={() => onAction('settle-negative', row)}
              disabled={acting === row.settlement_id}>
              {acting === row.settlement_id
                ? <span className="spinner" style={{ width: 12, height: 12 }} />
                : '💸 Marquer versé'}
            </button>
          )}

          {/* Flux B : LAVEX paie la laverie */}
          {type === 'laveries' && row.status === 'open' && (
            <button className="btn btn-primary btn-sm"
              onClick={() => onAction('settle', row)}
              disabled={acting === row.settlement_id}>
              {acting === row.settlement_id
                ? <span className="spinner" style={{ width: 12, height: 12 }} />
                : '💸 Marquer réglé'}
            </button>
          )}

          {/* Entreprises : confirmer paiement reçu */}
          {type === 'enterprises' && row.status === 'pending_confirmation' && (
            <button className="btn btn-success btn-sm"
              onClick={() => onAction('confirm', row)}
              disabled={acting === row.settlement_id}>
              {acting === row.settlement_id
                ? <span className="spinner" style={{ width: 12, height: 12 }} />
                : '✓ Confirmer paiement'}
            </button>
          )}

          <button className="btn btn-ghost btn-sm" onClick={() => onOpenDetail(row)}
            title="Voir le détail du flux">
            →
          </button>
        </div>
      </td>
    </tr>
  );
};

// ─── Panneau par type ─────────────────────────────────────────────────────────
const SettlementsPanel = ({ type }) => {
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('');
  const [search,       setSearch]       = useState('');
  const [page,         setPage]         = useState(1);
  const [acting,       setActing]       = useState(null);
  const [detailRow,    setDetailRow]    = useState(null);

  const fetchFn = {
    livreurs:    () => adminApi.getLivreurSettlements(statusFilter ? { status: statusFilter } : {}),
    laveries:    () => adminApi.getLaverieSettlements(statusFilter ? { status: statusFilter } : {}),
    enterprises: () => adminApi.getEnterpriseSettlements(statusFilter ? { status: statusFilter } : {}),
  }[type];

  const { data: raw, loading, error, refetch, setData } = useApi(fetchFn, [statusFilter]);
  const rows = raw?.data || [];

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(r =>
      !q || (r.livreur_name || r.laverie_name || r.enterprise_name || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const paged = paginate(filtered, page, 15);

  const pendingCount = rows.filter(r => r.status === 'pending_confirmation').length;
  const totalAmount  = rows.reduce((acc, r) => {
    if (type === 'livreurs')    return acc + Math.abs(getNetOwed(r));
    if (type === 'laveries')    return acc + (r.net_profit ?? 0);
    if (type === 'enterprises') return acc + (r.amount_due ?? 0);
    return acc;
  }, 0);

  const handleAction = async (action, row) => {
    setActing(row.settlement_id);
    try {
      if      (action === 'confirm'        && type === 'livreurs')    await adminApi.confirmLivreurSettlement(row.settlement_id);
      else if (action === 'settle-negative')                           await adminApi.settleNegativeLivreur(row.settlement_id);
      else if (action === 'settle')                                    await adminApi.settleLaverie(row.settlement_id);
      else if (action === 'confirm'        && type === 'enterprises') await adminApi.confirmEnterpriseSettlement(row.settlement_id);

      toast.success('Règlement mis à jour ✓');
      setData(prev => ({
        ...prev,
        data: prev.data.map(r =>
          r.settlement_id === row.settlement_id ? { ...r, status: 'settled' } : r
        ),
      }));
      // Mettre à jour la modale si ouverte sur ce règlement
      if (detailRow?.settlement_id === row.settlement_id) {
        setDetailRow(prev => ({ ...prev, status: 'settled' }));
      }
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setActing(null);
    }
  };

  // Explications contextuelles par type
  const FLUX_NOTES = {
    livreurs: {
      icon: '🛵',
      title: 'Flux de règlement livreurs',
      lines: [
        '💰 Livreur doit à LAVEX (positif) : le livreur paie via l\'app → statut passe à "En attente confirmation" → l\'admin confirme ici',
        '💸 LAVEX doit au livreur (négatif) : l\'admin envoie le virement manuellement puis clique "Marquer versé"',
      ],
    },
    laveries: {
      icon: '🏪',
      title: 'Flux de règlement laveries',
      lines: [
        '💸 LAVEX verse toujours à la laverie (jamais l\'inverse)',
        'L\'admin envoie le virement au RIB de la laverie puis clique "Marquer réglé"',
      ],
    },
    enterprises: {
      icon: '🏢',
      title: 'Flux de règlement entreprises',
      lines: [
        '💰 L\'entreprise vire à LAVEX suite à la facture mensuelle',
        'Quand le virement est reçu sur le compte LAVEX, l\'admin clique "Confirmer paiement"',
      ],
    },
  }[type];

  const headers = {
    livreurs:    ['Livreur / Période', 'Encaissé', 'Commission', 'Net dû à LAVEX', 'Statut', 'Actions'],
    laveries:    ['Laverie / Période', 'Revenus', 'Commission LAVEX', 'À verser', 'Statut', 'Actions'],
    enterprises: ['Entreprise / Période', 'Montant dû', 'Statut', 'Actions'],
  }[type];

  return (
    <div>
      {/* Note explicative flux */}
      <div style={{
        background: 'var(--bg3)', border: '1px solid var(--border)',
        borderRadius: 12, padding: '14px 18px', marginBottom: 20,
      }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
          {FLUX_NOTES.icon} {FLUX_NOTES.title}
        </div>
        {FLUX_NOTES.lines.map((line, i) => (
          <div key={i} style={{ fontSize: 12, color: 'var(--text2)', marginBottom: i < FLUX_NOTES.lines.length - 1 ? 4 : 0 }}>
            {line}
          </div>
        ))}
      </div>

      {pendingCount > 0 && (
        <div className="inline-alert inline-alert-yellow" style={{ marginBottom: 16 }}>
          <span>🕐</span>
          <div style={{ fontSize: 12 }}>
            <strong>{pendingCount} règlement(s)</strong> en attente de confirmation — vérifiez les paiements reçus et confirmez.
          </div>
        </div>
      )}

      {/* Stats */}
      {rows.length > 0 && (
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          {[
            { icon: '📋', label: 'Total règlements',        val: rows.length,             color: 'var(--text)'   },
            { icon: '🕐', label: 'En attente confirmation', val: pendingCount,             color: pendingCount > 0 ? 'var(--orange)' : 'var(--text3)' },
            { icon: '💰', label: 'Volume total',            val: formatPrice(totalAmount), color: 'var(--accent)' },
          ].map((s, i) => (
            <div key={i} className="stat-card">
              <div className="stat-card-accent" style={{ background: s.color }} />
              <div className="stat-icon" style={{ fontSize: 18 }}>{s.icon}</div>
              <div className="stat-value" style={{ color: s.color }}>{s.val}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filtres */}
      <div className="filter-bar" style={{ marginBottom: 16 }}>
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input className="search-input" placeholder="Rechercher..."
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select className="filter-select" value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">Tous les statuts</option>
          <option value="open">En cours</option>
          <option value="pending_confirmation">En attente confirmation</option>
          <option value="settled">Réglés</option>
        </select>
        <button className="btn btn-ghost btn-sm" onClick={refetch}>↺</button>
        <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 'auto' }}>
          {filtered.length} règlement(s) · cliquez une ligne pour le détail
        </span>
      </div>

      {/* Tableau */}
      <div className="card" style={{ padding: 0 }}>
        {loading ? <Loading text="Chargement des règlements..." />
          : error   ? <ApiError message={error} onRetry={refetch} />
          : (
            <>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {paged.items.map(r => (
                      <SettlementRow
                        key={r.settlement_id}
                        row={r}
                        type={type}
                        onAction={handleAction}
                        acting={acting}
                        onOpenDetail={setDetailRow}
                      />
                    ))}
                  </tbody>
                </table>
                {paged.items.length === 0 && !loading && (
                  <EmptyState icon="💰" title="Aucun règlement"
                    text="Les règlements apparaissent après la clôture hebdo/mensuelle (cron)." />
                )}
              </div>
              {paged.total > 0 && (
                <div style={{ padding: '0 16px 16px' }}>
                  <Pagination {...paged} onPage={setPage} />
                </div>
              )}
            </>
          )}
      </div>

      {/* Modale détail flux */}
      <SettlementDetailModal
        row={detailRow}
        type={type}
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        onAction={handleAction}
        acting={acting}
      />
    </div>
  );
};

// ─── Page principale ──────────────────────────────────────────────────────────
export const Reglements = () => {
  const [tab, setTab] = useState('livreurs');
  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 24, marginBottom: 6 }}>
          💰 Règlements & Paiements
        </div>
        <div style={{ color: 'var(--text2)', fontSize: 13 }}>
          Pilotage des règlements livreurs (hebdo), laveries (mensuel) et entreprises (mensuel).
          Les règlements sont générés automatiquement par le cron de clôture.
        </div>
      </div>

      <div style={{
        display: 'flex', gap: 4, marginBottom: 24,
        background: 'var(--bg3)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 4, width: 'fit-content',
      }}>
        {[
          { key: 'livreurs',    label: '🛵 Livreurs',    desc: 'Hebdomadaire · espèces/carte' },
          { key: 'laveries',    label: '🏪 Laveries',    desc: 'Mensuel · virement LAVEX → laverie' },
          { key: 'enterprises', label: '🏢 Entreprises', desc: 'Mensuel · virement entreprise → LAVEX' },
        ].map(t => (
          <button key={t.key}
            className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab(t.key)}
            style={{ borderRadius: 9, flexDirection: 'column', gap: 2, height: 'auto', padding: '8px 16px' }}>
            <span>{t.label}</span>
            <span style={{ fontSize: 10, opacity: 0.7, fontWeight: 400 }}>{t.desc}</span>
          </button>
        ))}
      </div>

      <SettlementsPanel key={tab} type={tab} />
    </div>
  );
};